import _ from 'lodash';
import moment from 'moment';

import { ADMIN_SCOPES } from '../constants/admin_scope_constants';
import { getPrettyHoursOrDaysSince, pluralizeCount } from './formatting';
import { getAnswerUpdate } from './links_to_check_flow_helpers';
import { SKIP_REASONS } from './links_to_check_helpers';
import { readable } from './url_data_agent_evaluations_helpers';
import { getApplySkipReasonCopy } from './url_data_agent_live_matches_helpers';
import { getUrlDataLink } from './url_data_helpers';

/* ────────────────────────────────────────────────────────────────
 * Where each participant link stands
 * ─────────────────────────────────────────────────────────────── */

export const OPPORTUNITY_LINK_STATE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'held', label: 'Held for people' },
  { value: 'awaiting', label: 'Waiting for approval' },
  { value: 'not_run', label: 'Not checked yet' },
  { value: 'running', label: 'APM checking' },
  { value: 'agent_matched', label: 'Matched by APM' },
  { value: 'person_matched', label: 'Matched by a person' },
  { value: 'dismissed', label: 'Dismissed' }
];

export const OPEN_LINK_STATES = ['held', 'awaiting', 'not_run', 'running'];

// The server's count keys for the link states (`links.notRun`, `links.personMatched`, …).
export const getLinkCountKey = state => _.camelCase(state);

export const getOpportunityLinkStateLabel = link => {
  if (link.state === 'agent_matched') return link.evaluation?.matchVerdict === 'new_product' ? 'Created by APM' : 'Matched by APM';
  if (link.state === 'person_matched') return `${link.evaluation?.appliedMatchStands ? 'Approved' : 'Matched'} by ${link.matchedBy?.name || 'a person'}`;
  return _.find(OPPORTUNITY_LINK_STATE_OPTIONS, { value: link.state })?.label || readable(link.state);
};

// Hold reasons the opportunity view adds to the apply-skip reasons the Live matches tab already explains.
const OPPORTUNITY_HOLD_REASON_COPY = {
  escalated: 'Escalated to a team lead',
  human_queue: 'In the catalog team’s queue; APM has not checked it',
  ignored_domain: 'APM does not read pages from this site',
  not_crawlable: 'The page could not be fetched',
  not_product_page: 'Marked as not a product page',
  intake_expired: 'APM did not get to it before its intake expired',
  run_failed: 'The APM run failed or timed out',
  run_undone: 'APM’s match was undone',
  run_skipped: 'APM skipped this link',
  suppressed_pair: 'Links from this site are never matched to the brand of APM’s pick',
  verdict_new_product: 'APM found no existing product and nothing was created',
  verdict_ambiguous: 'APM could not choose between close candidates',
  verdict_dismiss: 'APM judged this not to be a product page',
  verdict_sibling: 'APM’s pick was not applied',
  verdict_none: 'APM finished without a decision'
};

export const getOpportunityHoldReasonCopy = reason => {
  if (!reason) return null;
  if (OPPORTUNITY_HOLD_REASON_COPY[reason]) return OPPORTUNITY_HOLD_REASON_COPY[reason];
  if (reason.startsWith('verdict_')) return `APM’s answer was “${readable(reason.slice('verdict_'.length))}”; nothing was applied`;
  return _.upperFirst(getApplySkipReasonCopy(reason));
};

export const getOpportunityLinkReason = link => {
  if (link.state === 'held') return getOpportunityHoldReasonCopy(link.holdReason);
  if (link.state === 'awaiting') return 'APM proposed this match and is waiting for approval in the Queue tab';
  if (link.state === 'running') return 'APM is checking this link';
  if (link.state === 'not_run') return 'APM has not checked this link yet';
  if (link.state === 'dismissed') return `Dismissed ${moment(link.dismissedAt).format('MMM D')}`;
  return null;
};

/* ────────────────────────────────────────────────────────────────
 * What a person can do with a link, and which route does it
 * ─────────────────────────────────────────────────────────────── */

const isLiveEvaluation = link => link.evaluation?.applyLane === 'live';

const isMatched = link => ['agent_matched', 'person_matched'].includes(link.state);

export const hasAgentPick = link => Boolean(link.evaluation?.agentProduct) && ['sibling', 'new_variant'].includes(link.evaluation.matchVerdict);

// The site's rules never match the pick's brand, so either save would refuse the pick.
const canUseAgentPick = link => isLiveEvaluation(link) && hasAgentPick(link) && link.holdReason !== 'suppressed_pair';

// Every button on the row in display order; the one marked primary is the dark button. A button whose route
// saves through URL Data names the admin scope it needs, so the row can hold it back from reviewers without it.
export const getOpportunityLinkActions = link => {
  if (!link.urlStem) return [];
  const pickScope = getOpportunityLinkCorrectionRoute(link) === 'url_data' ? { scope: ADMIN_SCOPES.MATCH_PRODUCT } : {};
  const choose = { key: 'choose', display: 'Choose product…', ...pickScope };
  const dismiss = { key: 'dismiss', display: 'Dismiss link', scope: ADMIN_SCOPES.MATCH_PRODUCT };
  if (link.state === 'held') {
    return [...(canUseAgentPick(link) ? [{ key: 'use_pick', display: 'Use the agent’s pick', primary: true, ...pickScope }] : []), choose, dismiss];
  }
  // The send-back route only closes an open proposal; on a held row it would write a review and nothing else.
  if (link.state === 'awaiting') return [{ key: 'approve', display: 'Approve', primary: true }, choose, { key: 'send_to_team', display: 'Send to catalog team' }, dismiss];
  if (link.state === 'not_run') return [choose, dismiss];
  if (isMatched(link)) return [{ ...choose, display: 'Different…' }];
  return [];
};

// The review route closes APM's hold, or unwinds APM's standing match and its cascade, and records the outcome against the run;
// its match write skips escalated URLs, so those, like every link APM's live run does not own, save through URL Data.
export const getOpportunityLinkCorrectionRoute = link => {
  if (!isLiveEvaluation(link) || link.escalatedAt) return 'url_data';
  return isMatched(link) && !link.evaluation.appliedMatchStands ? 'url_data' : 'evaluation';
};

// The review route reverts APM's standing apply before saving a different product, and the revert reaches every stem the apply's cascade matched.
export const pickRevertsAgentApply = link => getOpportunityLinkCorrectionRoute(link) === 'evaluation' && Boolean(link.evaluation.appliedMatchStands);

// The Live matches picker loads APM's shortlist with getEvaluation(row.id), so id is the link's evaluation (none before APM checked it).
export const getOpportunityLinkPickerRow = link => ({
  id: link.evaluation?.id,
  agentProductName: link.evaluation?.agentProductName,
  agentBrandName: link.evaluation?.agentBrandName,
  productName: link.page.title
});

export const SKIP_REASON_OPTIONS = SKIP_REASONS.map(reason => ({ value: reason.dismissReason, label: reason.display }));

// The PUT /api/UrlData/:hashedUrlStem body for a pick or a dismissal, as Links to Check writes it.
export const getOpportunityLinkUpdate = (answer, link) => {
  const update = getAnswerUpdate(answer);
  return { ...update, metadata: { ...update.metadata, agentSuggestionShown: hasAgentPick(link) } };
};

export const getOpportunityLinkUrls = links => _.uniq(links.map(getUrlDataLink).filter(Boolean));

// The page after one link was re-read: swapped in place, or dropped once it has left the state filter, with the counts moved along.
export const replaceOpportunityLink = (report, hashedUrlStem, link) => {
  const previous = report.rows.find(row => row.hashedUrlStem === hashedUrlStem);
  if (!previous) return report;
  const stays = Boolean(link) && isInLinkStateFilter(link.state, report.linkState);
  return {
    ...report,
    links: addToLinkCounts(addToLinkCounts(report.links, previous.state, -1), link?.state, 1),
    rows: stays ? report.rows.map(row => (row === previous ? link : row)) : report.rows.filter(row => row !== previous),
    total: stays ? report.total : report.total - 1
  };
};

const isInLinkStateFilter = (state, filter) => filter === 'all' || (filter === 'open' ? OPEN_LINK_STATES.includes(state) : state === filter);

const addToLinkCounts = (links, state, step) => {
  if (!state) return links;
  const key = getLinkCountKey(state);
  return { ...links, total: links.total + step, open: links.open + (OPEN_LINK_STATES.includes(state) ? step : 0), [key]: links[key] + step };
};

/* ────────────────────────────────────────────────────────────────
 * The opportunity list
 * ─────────────────────────────────────────────────────────────── */

export const OPPORTUNITY_SORT_OPTIONS = [
  { value: 'ending', label: 'Ending latest' },
  { value: 'open', label: 'Most open links' },
  { value: 'links', label: 'Most links' },
  { value: 'title', label: 'Name' }
];

// Rows whose counts have not loaded sort last.
const OPPORTUNITY_SORT_ORDERS = {
  ending: [[row => moment(row.trackingEndsAt).valueOf(), 'id'], ['desc', 'desc']],
  open: [[row => row.links?.open ?? -1, row => row.links?.total ?? -1], ['desc', 'desc']],
  links: [[row => row.links?.total ?? -1, row => row.links?.open ?? -1], ['desc', 'desc']],
  title: [[row => String(row.title || '').toLowerCase()], ['asc']]
};

export const sortOpportunities = (rows, sort) => {
  const [iteratees, orders] = OPPORTUNITY_SORT_ORDERS[sort] || OPPORTUNITY_SORT_ORDERS.ending;
  return _.orderBy(rows || [], iteratees, orders);
};

export const getOpportunityWindow = (opportunity, now = moment()) => {
  const start = moment(opportunity.trackingStartsAt);
  const end = moment(opportunity.trackingEndsAt);
  const sameYear = start.year() === end.year();
  const range = `${start.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
  if (now.isBefore(start)) return { range, status: 'upcoming', label: `Starts in ${getPrettyHoursOrDaysSince(now, start)}` };
  if (now.isAfter(end)) return { range, status: 'ended', label: `Ended ${getPrettyHoursOrDaysSince(end, now)} ago` };
  return { range, status: 'live', label: `Live · ${getPrettyHoursOrDaysSince(now, end)} left` };
};

export const getParticipantsLabel = link => {
  const names = (link.participants || []).map(participant => participant.name);
  const more = link.participantCount - names.length;
  const who = names.length ? `${names.join(', ')}${more > 0 ? ` +${more}` : ''}` : pluralizeCount(link.participantCount, 'creator');
  return link.pinCount > link.participantCount ? `${who} · ${pluralizeCount(link.pinCount, 'pin')}` : who;
};
